import { useCallback, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { UserPlus, Edit2 } from "lucide-react";
import { Avatar } from "@/components/atoms/Avatar";
import { Badge } from "@/components/atoms/Badge";
import { Button } from "@/components/atoms/Button";
import { CopyButton } from "@/components/atoms/CopyButton";
import { Select } from "@/components/atoms/Select";
import { Tooltip } from "@/components/atoms/Tooltip";
import { SavedFiltersButton } from "@/components/molecules/SavedFiltersButton";
import { usersService } from "@/services/usersService";
import { formatRelativeTime } from "@/lib/format";
import { SimpleListPage } from "@/modules/_shared/SimpleListPage";
import { AddTeamMemberDialog } from "@/modules/profile/AddTeamMemberDialog";
import { useProfileStore } from "@/store/profileStore";
import { useKeycloak } from "@/modules/auth/context/KeycloakContext";
import type { Column } from "@/components/organisms/DataTable";
import type { PageRequest } from "@/types/api";
import type { UserFilter, UserSummary } from "@/types/entities";
import { EditUserIntentDialog } from "./EditUserIntentDialog";
import {
  parseUserFilterFromSearchParams,
  USER_FILTER_PARAM_KEYS,
  userFilterUrlSyncConfig,
} from "./userFilterParams";
import { useCreateShortcut } from "@/hooks/useCreateShortcut";
import { formatPrimaryShortcut } from "@/lib/keyboardShortcut";

const columns: Column<UserSummary>[] = [
  {
    id: "name",
    header: "User",
    sortable: true,
    width: "max-w-[22rem] w-[22rem]",
    cell: (r) => (
      <div className="flex min-w-0 items-center gap-3">
        <Avatar name={r.name} size="sm" colorful />
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="truncate font-medium text-bridge-fg" title={r.name ?? undefined}>
            {r.name ?? "(unnamed)"}
          </div>
          <div className="group/copy flex min-w-0 items-center gap-1">
            <div className="truncate text-xs text-bridge-muted" title={r.email ?? undefined}>
              {r.email ?? "no email"}
            </div>
            <CopyButton value={r.email} label="Email" />
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "id",
    header: "User ID",
    width: "w-[220px]",
    cell: (r) => (
      <div className="group/copy flex min-w-0 items-center gap-1">
        <span className="truncate text-xs text-slate-700" title={r.id}>{r.id}</span>
        <CopyButton
          value={r.id}
          label="User ID"
          successMessage="Record ID copied to clipboard"
        />
      </div>
    ),
  },
  {
    id: "role",
    header: "Role",
    sortable: true,
    width: "w-[12rem]",
    cell: (r) => {
      if (!r.role) return <span className="text-bridge-subtle">—</span>;
      const role = r.role.toUpperCase();
      let tone: "neutral" | "accent" | "success" | "warning" | "danger" | "info" = "neutral";
      if (role.includes("ADMIN")) tone = "danger";
      else if (role.includes("MANAGER")) tone = "info";
      else if (role.includes("SUPPORT")) tone = "accent";
      else if (role.includes("NEGOTIATOR")) tone = "neutral";

      return (
        <Badge tone={tone} className="max-w-full whitespace-nowrap font-bold">
          {r.role.replace(/_/g, " ")}
        </Badge>
      );
    },
  },
  {
    id: "officeId",
    header: "Office",
    width: "max-w-[14rem] w-[14rem]",
    cell: (r) =>
      r.officeId ? (
        <span className="block truncate" title={r.officeId}>
          {r.officeId}
        </span>
      ) : (
        "—"
      ),
  },
  {
    id: "branches",
    header: "Branch",
    width: "max-w-[18rem] w-[18rem]",
    cell: (r) => {
      const branches = r.branches ?? [];
      if (branches.length === 0) return <span className="text-bridge-subtle">—</span>;
      return (
        <div
          className="flex flex-wrap justify-center gap-1"
          title={branches.map((b) => b.name ?? b.id).join(", ")}
        >
          {branches.map((b) => (
            <Badge key={b.id} tone="neutral">
              {b.name ?? "—"}
            </Badge>
          ))}
        </div>
      );
    },
  },
  {
    id: "active",
    header: "Status",
    width: "w-[7rem]",
    cell: (r) =>
      r.active === true ? <Badge tone="success">Active</Badge>
      : r.active === false ? <Badge tone="neutral">Inactive</Badge>
      : <span className="text-bridge-subtle">—</span>,
  },
  {
    id: "atlasModifiedAt",
    header: "Updated",
    sortable: true,
    align: "right",
    width: "w-[8rem]",
    cell: (r) => <span className="text-bridge-muted">{formatRelativeTime(r.atlasModifiedAt)}</span>,
  },
];

export function UsersPage() {
  const [searchParams] = useSearchParams();
  const [initialFilter] = useState(() => parseUserFilterFromSearchParams(searchParams));
  const { keycloak } = useKeycloak();
  const { profileData: profile } = useProfileStore();
  const [addTeamMemberOpen, setAddTeamMemberOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserSummary | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const openCreateUser = useCallback(() => {
    setEditingUser(null);
    setAddTeamMemberOpen(true);
  }, []);
  useCreateShortcut(openCreateUser);

  const email = keycloak?.tokenParsed?.email as string | undefined;

  const dynamicColumns = useMemo<Column<UserSummary>[]>(() => [
    ...columns,
    {
      id: "intent",
      header: "Intent",
      align: "center",
      width: "max-w-[14rem] w-[14rem]",
      cell: (r) => {
        if (!r.userIntent || r.userIntent.length === 0) return <span className="text-bridge-subtle">—</span>;
        return (
          <div
            className="flex w-full flex-wrap items-center justify-center gap-1"
            title={r.userIntent.join(", ")}
          >
            {r.userIntent.map((intent) => (
              <Badge key={intent} tone="neutral" className="whitespace-normal break-words">
                {intent}
              </Badge>
            ))}
          </div>
        );
      }
    },
    {
      id: "actions",
      header: "",
      align: "right",
      width: "w-[3rem]",
      cell: (r) => (
        <button 
          onClick={(e) => { e.stopPropagation(); setEditingUser(r); }}
          className="text-bridge-muted hover:text-bridge-fg p-1 rounded transition-colors"
          title="Edit user intent"
        >
          <Edit2 size={16} />
        </button>
      )
    }
  ], []);

  return (
    <>
      <SimpleListPage<UserSummary, UserFilter>
        key={refreshKey}
        title="Users"
        fetcher={usersService.list}
        initialFilter={initialFilter}
        filterUrlSync={userFilterUrlSyncConfig}
        initialPage={{ page: 0, size: 30, sort: "atlasModifiedAt", direction: "desc" } as PageRequest}
        globalFilterConfig={{ legalEntity: true, branch: true }}
        scopePage="users"
        columns={dynamicColumns}
        tableCustomization={{
          tableId: "users",
          defaultHiddenColumnIds: ["id"],
          lockedColumnIds: ["name", "actions"],
          pinnedStartColumnIds: ["name"],
          pinnedEndColumnIds: ["actions"],
        }}
        rowKey={(r) => r.id}
        searchPlaceholder="Search by name, email, role..."
        searchTooltip="Search users by name, email or role."
        emptyTitle="No users match"
        showPageLoader
        toolbar={({ filter, setFilter }) => (
          <Tooltip side="top" hoverOnly content="Show users by the lead intent they are assigned to handle.">
            <div>
              <Select
                className="w-40"
                placeholder="All Intents"
                value={filter.userIntent ?? ""}
                onChange={(e) => setFilter({ userIntent: e.target.value || undefined })}
                options={[
                  { value: "SALES", label: "Sales" },
                  { value: "LETTINGS", label: "Lettings" },
                  { value: "GENERAL", label: "General" },
                  { value: "UNSPECIFIED", label: "Unspecified" },
                ]}
              />
            </div>
          </Tooltip>
        )}
        actions={
          <>
            <SavedFiltersButton
              pageName="users"
              filterParamKeys={USER_FILTER_PARAM_KEYS}
              scopePage="users"
            />
            <Tooltip content={`Add team member (${formatPrimaryShortcut("k")})`} side="bottom">
              <Button
                variant="primary"
                size="sm"
                onClick={openCreateUser}
              >
                <UserPlus size={16} className="mr-2" /> Add Team Member
              </Button>
            </Tooltip>
          </>
        }
      />

      <AddTeamMemberDialog
        open={addTeamMemberOpen}
        onClose={() => setAddTeamMemberOpen(false)}
        onCreated={() => {
          setRefreshKey((k) => k + 1);
        }}
        callerEmail={email ?? ""}
        legalEntities={profile?.legalEntities ?? []}
      />

      <EditUserIntentDialog
        open={!!editingUser}
        user={editingUser}
        onClose={() => setEditingUser(null)}
        onSaved={() => {
          setEditingUser(null);
          setRefreshKey((k) => k + 1);
        }}
      />
    </>
  );
}
